import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, switchMap, map, catchError } from 'rxjs/operators';
import { NbDialogService } from '@nebular/theme';
import { CompromissosCacheService } from '../../../../../shared/services/compromissos-cache.service';
import { CompromissosCustomizadosCacheService, CompromissoCustomizado } from '../../../../../shared/services/compromissos-customizados-cache.service';
import { RegistroContatoSimplificadoResult } from '../../../../../pages/negocio/cliente/components/registro-contato/shared/models/result/RegistroContatoSimplificadoResult';
import { ModalCompromissoCustomizadoComponent } from './modal-compromisso-customizado/modal-compromisso-customizado.component';
import { AnalistaDataService } from '../../../../../../services/v1/AnalistaController/analista-data.service';
import { TutorialService } from '../../../../../shared/services/tutorial.service';


interface Compromisso extends RegistroContatoSimplificadoResult {
}

@Component({
  selector: 'ngx-agenda-compromissos',
  templateUrl: './agenda-compromissos.component.html',
  styleUrls: ['./agenda-compromissos.component.scss']
})
export class AgendaCompromissosComponent implements OnInit, OnDestroy {
  @Output() fecharOverlay = new EventEmitter<void>();

  private destroy$: Subject<void> = new Subject<void>();

  carregando: boolean = true;
  dadosCarregados: boolean = false;
  compromissos: Compromisso[] = [];
  compromissosHoje: Compromisso[] = [];
  compromissosCustomizados: CompromissoCustomizado[] = [];
  compromissosCustomizadosHoje: CompromissoCustomizado[] = [];
  diasComCompromissos: Set<string> = new Set();
  diasComCompromissosCustomizados: Set<string> = new Set();

  mostrarCalendario: boolean = true;
  compromissoSelecionado: Compromisso | null = null;
  compromissoCustomizadoSelecionado: CompromissoCustomizado | null = null;
  diaSelecionado: any = null;
  compromissosNoDiaSelecionado: Compromisso[] = [];
  compromissosCustomizadosNoDiaSelecionado: CompromissoCustomizado[] = [];

  // Calendar properties
  mesAtual: Date = new Date();
  diasDoMes: any[] = [];
  nomesMeses: string[] = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  constructor(
    private compromissosCacheService: CompromissosCacheService,
    private compromissosCustomizadosService: CompromissosCustomizadosCacheService,
    private dialogService: NbDialogService,
    private analistaService: AnalistaDataService,
    private tutorialService: TutorialService
  ) { }

  ngOnInit(): void {
    this.gerarCalendario();
  }

  inicializar(): void {
    this.mostrarCalendario = true;
    this.compromissoSelecionado = null;
    this.compromissoCustomizadoSelecionado = null;
    this.diaSelecionado = null;
    this.compromissosNoDiaSelecionado = [];
    this.compromissosCustomizadosNoDiaSelecionado = [];

    // Only show tutorial when calendar is visible
    this.tutorialService.checkAndShowTutorialIfNeeded();

    if (!this.dadosCarregados) {
      this.carregarCompromissos();
      this.carregarCompromissosCustomizados();
    } else {
      // Se os dados já foram carregados, apenas atualiza com o cache atual
      this.atualizarDados();
    }
  }

  forcarRecarregamento(): void {
    this.forcarRecarregamentoAsync()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  forcarRecarregamentoAsync(): Observable<boolean> {
    this.dadosCarregados = false;
    this.carregando = true;

    return this.compromissosCacheService.carregarCompromissos(true)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(compromissos => {
          this.compromissos = compromissos;
          this.processarCompromissos();

          return this.compromissosCustomizadosService.carregarCompromissos(true)
            .pipe(takeUntil(this.destroy$));
        }),
        map(compromissosCustomizados => {
          this.compromissosCustomizados = compromissosCustomizados;
          this.processarCompromissosCustomizados();
          this.gerarCalendario();
          this.dadosCarregados = true;
          this.carregando = false;
          console.log("forcarRecarregamento good");
          return true;
        }),
        catchError(error => {
          console.error('Erro ao carregar compromissos:', error);
          this.gerarCalendario();
          this.carregando = false;
          this.dadosCarregados = true;
          console.log("forcarRecarregamento bad");
          return of(false);
        })
      );
  }

  atualizarDados(): void {
    this.carregando = true;
    this.dadosCarregados = false;
    // Subscreve às mudanças no cache
    this.compromissosCacheService.compromissos$
      .pipe(takeUntil(this.destroy$))
      .subscribe(compromissos => {
        this.compromissos = compromissos;
        this.processarCompromissos();
        this.gerarCalendario();
        this.carregando = false;
        this.dadosCarregados = true;
      });

    this.compromissosCustomizadosService.compromissos$
      .pipe(takeUntil(this.destroy$))
      .subscribe(compromissosCustomizados => {
        this.compromissosCustomizados = compromissosCustomizados;
        this.processarCompromissosCustomizados();
        this.gerarCalendario();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarCompromissos(): void {
    this.carregando = true;

    this.compromissosCacheService.carregarCompromissos()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        compromissos => {
          this.compromissos = compromissos;
          this.processarCompromissos();
          this.gerarCalendario();
          this.dadosCarregados = true;
          this.carregando = false;
        },
        error => {
          console.error('Erro ao carregar compromissos:', error);
          this.carregando = false;
          this.dadosCarregados = false;
        }
      );
  }

  carregarCompromissosCustomizados(): void {
    this.carregando = true;
    this.compromissosCustomizadosService.carregarCompromissos()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        compromissosCustomizados => {
          this.compromissosCustomizados = compromissosCustomizados;
          this.processarCompromissosCustomizados();
          this.gerarCalendario();
          this.dadosCarregados = true;
          this.carregando = false;
        },
        error => {
          console.error('Erro ao carregar compromissos customizados:', error);
          this.carregando = false;
          this.dadosCarregados = false;
        }
      );
  }

  processarCompromissos(): void {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    this.compromissosHoje = this.compromissos.filter(compromisso => {
      const dataCompromisso = new Date(compromisso.dtRetorno);
      dataCompromisso.setHours(0, 0, 0, 0);
      return dataCompromisso.getTime() === hoje.getTime();
    }).sort((a, b) => new Date(a.dtRetorno).getTime() - new Date(b.dtRetorno).getTime());

    this.diasComCompromissos.clear();
    this.compromissos.forEach(compromisso => {
      const data = new Date(compromisso.dtRetorno);
      const dataString = `${data.getFullYear()}-${(data.getMonth() + 1).toString().padStart(2, '0')}-${data.getDate().toString().padStart(2, '0')}`;
      this.diasComCompromissos.add(dataString);
    });
  }

  processarCompromissosCustomizados(): void {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    this.compromissosCustomizadosHoje = this.compromissosCustomizados.filter(compromisso => {
      if (!compromisso.data_compromisso) return false;
      const dataCompromisso = new Date(compromisso.data_compromisso);
      dataCompromisso.setHours(0, 0, 0, 0);
      return dataCompromisso.getTime() === hoje.getTime();
    }).sort((a, b) => new Date(a.data_compromisso!).getTime() - new Date(b.data_compromisso!).getTime());

    this.diasComCompromissosCustomizados.clear();
    this.compromissosCustomizados.forEach(compromisso => {
      if (compromisso.data_compromisso) {
        const data = new Date(compromisso.data_compromisso);
        const dataString = `${data.getFullYear()}-${(data.getMonth() + 1).toString().padStart(2, '0')}-${data.getDate().toString().padStart(2, '0')}`;
        this.diasComCompromissosCustomizados.add(dataString);
      }
    });
  }

  gerarCalendario(): void {
    const ano = this.mesAtual.getFullYear();
    const mes = this.mesAtual.getMonth();

    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const primeiroDiaSemana = primeiroDia.getDay();

    this.diasDoMes = [];

    for (let i = primeiroDiaSemana - 1; i >= 0; i--) {
      const dia = new Date(ano, mes, -i);
      this.diasDoMes.push({
        numero: dia.getDate(),
        data: dia,
        mesAtual: false,
        temCompromisso: false,
        quantidadeCompromissos: 0
      });
    }

    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      const data = new Date(ano, mes, dia);
      const dataString = `${ano}-${(mes + 1).toString().padStart(2, '0')}-${dia.toString().padStart(2, '0')}`;
      const quantidadeCompromissos = this.obterQuantidadeCompromissosPorDia(data);
      const quantidadeCompromissosCustomizados = this.obterQuantidadeCompromissosCustomizadosPorDia(data);

      this.diasDoMes.push({
        numero: dia,
        data: data,
        mesAtual: true,
        temCompromisso: this.diasComCompromissos.has(dataString) || this.diasComCompromissosCustomizados.has(dataString),
        quantidadeCompromissos: quantidadeCompromissos,
        quantidadeCompromissosCustomizados: quantidadeCompromissosCustomizados
      });
    }

    // Completar com dias do próximo mês se necessário
    const totalDias = this.diasDoMes.length;
    const diasRestantes = 42 - totalDias; // 6 semanas * 7 dias

    for (let dia = 1; dia <= diasRestantes; dia++) {
      const data = new Date(ano, mes + 1, dia);
      this.diasDoMes.push({
        numero: dia,
        data: data,
        mesAtual: false,
        temCompromisso: false,
        quantidadeCompromissos: 0
      });
    }
  }

  mesAnterior(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() - 1, 1);
    this.gerarCalendario();
  }

  proximoMes(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 1);
    this.gerarCalendario();
  }

  selecionarDia(dia: any): void {
    if (!dia.mesAtual || !dia.temCompromisso) {
      this.voltarParaCalendario();
      return;
    }

    this.diaSelecionado = dia;

    // Carregar compromissos regulares do dia selecionado
    this.compromissosNoDiaSelecionado = this.compromissos.filter(compromisso => {
      const dataCompromisso = new Date(compromisso.dtRetorno);
      return dataCompromisso.getDate() === dia.numero &&
        dataCompromisso.getMonth() === this.mesAtual.getMonth() &&
        dataCompromisso.getFullYear() === this.mesAtual.getFullYear();
    }).sort((a, b) => new Date(a.dtRetorno).getTime() - new Date(b.dtRetorno).getTime());

    // Carregar compromissos customizados do dia selecionado
    this.compromissosCustomizadosNoDiaSelecionado = this.compromissosCustomizados.filter(compromisso => {
      if (!compromisso.data_compromisso) return false;
      const dataCompromisso = new Date(compromisso.data_compromisso);
      return dataCompromisso.getDate() === dia.numero &&
        dataCompromisso.getMonth() === this.mesAtual.getMonth() &&
        dataCompromisso.getFullYear() === this.mesAtual.getFullYear();
    }).sort((a, b) => new Date(a.data_compromisso!).getTime() - new Date(b.data_compromisso!).getTime());

    this.mostrarCalendario = false;
    // Hide tutorial when leaving calendar view
    this.tutorialService.hideTutorial();

    this.compromissoSelecionado = this.compromissosNoDiaSelecionado[0] || null;
    if (!this.compromissoSelecionado) {
      this.compromissoCustomizadoSelecionado = this.compromissosCustomizadosNoDiaSelecionado[0] || null;
    }
  }

  selecionarCompromisso(compromisso: Compromisso): void {
    this.compromissoSelecionado = compromisso;
    this.compromissoCustomizadoSelecionado = null;
  }

  selecionarCompromissoCustomizado(compromisso: CompromissoCustomizado): void {
    this.compromissoCustomizadoSelecionado = compromisso;
    this.compromissoSelecionado = null;
    this.mostrarCalendario = false;
    // Hide tutorial when leaving calendar view
    this.tutorialService.hideTutorial();
  }

  selecionarCompromissoCustomizadoHoje(compromisso: CompromissoCustomizado): void {
    this.compromissoCustomizadoSelecionado = compromisso;
    this.compromissoSelecionado = null;
    this.mostrarCalendario = false;
    this.tutorialService.hideTutorial();
    this.diaSelecionado = null;
  }

  voltarParaLista(): void {
    this.compromissoSelecionado = null;
    this.compromissoCustomizadoSelecionado = null;
    if (this.compromissosNoDiaSelecionado.length > 0) {
      this.compromissoSelecionado = this.compromissosNoDiaSelecionado[0];
    } else if (this.compromissosCustomizadosNoDiaSelecionado.length > 0) {
      this.compromissoCustomizadoSelecionado = this.compromissosCustomizadosNoDiaSelecionado[0];
    } else {
      this.voltarParaCalendario();
    }
  }

  voltarParaCalendario(): void {
    this.mostrarCalendario = true;
    this.compromissoSelecionado = null;
    this.compromissoCustomizadoSelecionado = null;
    this.diaSelecionado = null;
    this.compromissosNoDiaSelecionado = [];
    this.compromissosCustomizadosNoDiaSelecionado = [];

    // Show tutorial when returning to calendar view
    this.tutorialService.checkAndShowTutorialIfNeeded();
  }

  get temCompromissosHoje(): boolean {
    return this.compromissosHoje.length > 0;
  }

  get temCompromissosCustomizadosHoje(): boolean {
    return this.compromissosCustomizadosHoje.length > 0;
  }

  get nomeDoMes(): string {
    return this.nomesMeses[this.mesAtual.getMonth()];
  }

  get anoAtual(): number {
    return this.mesAtual.getFullYear();
  }

  obterClasseCompromisso(compromisso: Compromisso): string {
    const agora = new Date();
    const horaCompromisso = new Date(compromisso.dtRetorno);

    if (horaCompromisso < agora) {
      return 'appointment-past';
    }

    const proximoCompromisso = this.obterProximoCompromisso();
    if (proximoCompromisso && this.saoMesmoCompromisso(compromisso, proximoCompromisso)) {
      return 'appointment-next';
    }

    return 'appointment-future';
  }

  private obterProximoCompromisso(): Compromisso | null {
    const agora = new Date();
    const compromissosFuturos = this.compromissosHoje.filter(c => new Date(c.dtRetorno) >= agora);
    return compromissosFuturos.length > 0 ? compromissosFuturos[0] : null;
  }

  private saoMesmoCompromisso(compromisso1: Compromisso, compromisso2: Compromisso): boolean {
    return compromisso1.nomeCliente === compromisso2.nomeCliente &&
      compromisso1.dtRetorno === compromisso2.dtRetorno &&
      compromisso1.feedback === compromisso2.feedback;
  }

  obterQuantidadeCompromissosPorDia(data: Date): number {
    return this.compromissos.filter(compromisso => {
      const dataCompromisso = new Date(compromisso.dtRetorno);
      return dataCompromisso.getDate() === data.getDate() &&
        dataCompromisso.getMonth() === data.getMonth() &&
        dataCompromisso.getFullYear() === data.getFullYear();
    }).length;
  }

  obterQuantidadeCompromissosCustomizadosPorDia(data: Date): number {
    return this.compromissosCustomizados.filter(compromisso => {
      if (!compromisso.data_compromisso) return false;
      const dataCompromisso = new Date(compromisso.data_compromisso);
      return dataCompromisso.getDate() === data.getDate() &&
        dataCompromisso.getMonth() === data.getMonth() &&
        dataCompromisso.getFullYear() === data.getFullYear();
    }).length;
  }

  abrirCompromissoEspecifico(compromisso: Compromisso): void {
    if (!this.dadosCarregados) {
      this.forcarRecarregamentoAsync()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.abrirCompromissoEspecifico(compromisso);
        });
      return;
    }

    // Set the specific appointment as selected
    const dateDateRetorno = new Date(compromisso.dtRetorno);
    const dia = this.diasDoMes.find(dia => dia.data.getDate() === dateDateRetorno.getDate() && dia.data.getMonth() === dateDateRetorno.getMonth() && dia.data.getFullYear() === dateDateRetorno.getFullYear());

    this.selecionarDia(dia);
    this.selecionarCompromisso(compromisso);
  }

  adicionarCompromisso(dia: any, event: Event): void {
    // Prevent the day selection event from firing
    event.stopPropagation();

    this.abrirModalCompromissoCustomizado(dia.data);
  }

  abrirCompromissoCustomizado(compromisso: CompromissoCustomizado): void {
    if (!this.dadosCarregados) {
      this.forcarRecarregamentoAsync()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.abrirCompromissoCustomizado(compromisso);
        });
      return;
    }
    // Set the specific appointment as selected
    const dataCompromisso = new Date(compromisso.data_compromisso);
    const dia = this.diasDoMes.find(dia => dia.data.getDate() === dataCompromisso.getDate() && dia.data.getMonth() === dataCompromisso.getMonth() && dia.data.getFullYear() === dataCompromisso.getFullYear());
    this.selecionarDia(dia);
    this.selecionarCompromissoCustomizado(compromisso);
  }

  atualizaDiaSelecionado(): void {
    const novo_dia = this.diasDoMes.find(dia => dia.data.getDate() === this.diaSelecionado.numero && dia.data.getMonth() === this.diaSelecionado.data.getMonth() && dia.data.getFullYear() === this.diaSelecionado.data.getFullYear());
    this.selecionarDia(novo_dia);
  }

  abrirModalCompromissoCustomizado(dataSelecionada: Date, compromisso?: CompromissoCustomizado): void {
    const modalRef = this.dialogService.open(ModalCompromissoCustomizadoComponent, {
      context: {
        dataSelecionada: dataSelecionada,
        compromisso: compromisso,
        modoEdicao: !!compromisso
      },
      closeOnBackdropClick: false,
      closeOnEsc: true
    });

    modalRef.onClose.subscribe(result => {
      if (result && result.success) {
        if (result.action === 'create' || result.action === 'update') {
          this.forcarRecarregamentoAsync()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.processarCompromissosCustomizados();
              this.gerarCalendario();
              this.atualizaDiaSelecionado();
            });
        }
      }
    });
  }

  editarCompromissoCustomizado(compromisso: CompromissoCustomizado): void {
    if (compromisso.data_compromisso) {
      const dataSelecionada = new Date(compromisso.data_compromisso);
      this.abrirModalCompromissoCustomizado(dataSelecionada, compromisso);
    }
  }

  editarCompromissoCustomizadoSelecionado(): void {
    if (this.compromissoCustomizadoSelecionado) {
      this.editarCompromissoCustomizado(this.compromissoCustomizadoSelecionado);
    }
  }

  removerCompromissoCustomizadoSelecionado(): void {
    if (!this.compromissoCustomizadoSelecionado?.id) {
      return;
    }
    if (confirm('Tem certeza que deseja deletar este compromisso?')) {
      this.analistaService.DeletarCompromisso(this.compromissoCustomizadoSelecionado.id).subscribe(
        response => {
          if (response.Success) {
            this.forcarRecarregamentoAsync()
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => {
                this.processarCompromissosCustomizados();
                this.gerarCalendario();
                this.atualizaDiaSelecionado();
              });
          } else {
            console.error('Erro ao deletar compromisso:', response.message);
          }
        },
        error => {
          console.error('Erro ao deletar compromisso:', error);
        }
      );
    }
  }
} 